import { getLanguageForTagBCP47, getLanguageForCountry, ipCountryQuery, configQuery, setLanguage, setTranslations, queryMappedTranslations } from '@gig/magic-common';
import { LANGUAGE_QUERY, SINGLE_LANGUAGE_QUERY, LANGUAGE_COMMAND_CHANGE, USER_LOGIN_KEYCLOAK, LANGUAGE_UPDATED, VUE_I_18N_QUERY } from '@gig/magic-messages';
import { __awaiter, __generator } from 'tslib';
import { magic } from '@gig/magic-sdk';
import VueI18n from 'vue-i18n';
import { Language } from '@gig/magic-sdk/dist/adapter/users/language';
import Vue from 'vue';
import { getKeycloakLocale, setKeycloakLocale } from '../../utils/keycloak.js';

var USER_LANGUAGE_KEY = 'pw-user-language';
var LanguageService = /** @class */ (function () {
    function LanguageService() {
        var _this = this;
        this.isSingleLanguage = false;
        this.isBot = /Prerender/i.test(navigator.userAgent);
        this.detectLanguage().then(function (language) {
            _this.language = language;
            // Locale initialization
            Vue.use(VueI18n);
            _this.i18n = new VueI18n({
                messages: {
                    en: {},
                },
                locale: _this.language,
                fallbackLocale: Language.ENGLISH,
            });
            _this.registerHandlers();
            _this.initialize();
        });
    }
    LanguageService.prototype.detectLanguage = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var localStorageLang, _b, country, lockedCountries, languages, getLanguageFromUrl, urlLang_1, locale, lang_1, lang, user, urlLang;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        localStorageLang = window.localStorage.getItem(USER_LANGUAGE_KEY);
                        return [4 /*yield*/, Promise.all([
                                ipCountryQuery(),
                                configQuery('countries.lockedCountries'),
                                configQuery('languages'),
                            ])];
                    case 1:
                        _b = _c.sent(), country = _b[0], lockedCountries = _b[1], languages = _b[2];
                        this.isSingleLanguage = ((_a = languages === null || languages === void 0 ? void 0 : languages.available) === null || _a === void 0 ? void 0 : _a.length) === 1;
                        getLanguageFromUrl = function () {
                            var urlRegexp = new RegExp("^/(" + languages.available.join('|') + ")(/|$)");
                            var groups = window.location.pathname.match(urlRegexp);
                            return groups && groups[1] ? groups[1] : null;
                        };
                        // For bots, prioritize language from URL
                        if (this.isBot) {
                            urlLang_1 = getLanguageFromUrl();
                            if (urlLang_1) {
                                return [2 /*return*/, urlLang_1];
                            }
                        }
                        if (window.kcContext) {
                            locale = getKeycloakLocale();
                            return [2 /*return*/, getLanguageForTagBCP47(locale)];
                        }
                        if (this.isSingleLanguage && (languages === null || languages === void 0 ? void 0 : languages.available)) {
                            lang_1 = languages.available[0];
                            window.localStorage.setItem(USER_LANGUAGE_KEY, lang_1);
                            return [2 /*return*/, lang_1];
                        }
                        // Return the default country language if the country is language locked
                        if (country && lockedCountries.indexOf(country) > -1) {
                            return [2 /*return*/, getLanguageForCountry(country)];
                        }
                        lang = localStorageLang
                            ? localStorageLang
                            : getLanguageForCountry(country);
                        user = magic.session.getUser();
                        if (user) {
                            lang = user.lang;
                        }
                        urlLang = getLanguageFromUrl();
                        if (urlLang) {
                            lang = urlLang;
                        }
                        // If no valid locale detected, revert to default
                        if (!lang || languages.available.indexOf(lang) === -1) {
                            lang = languages.default;
                        }
                        // Check if the detected language is 4 letters (e.g., en-US)
                        if (lang.length === 2) {
                            lang = lang.toLowerCase();
                        }
                        else if (lang.length === 5 && lang.indexOf('-') === 2) {
                            // Check for the pattern xx-XX
                            lang = lang.substring(0, 2).toLowerCase() + "-" + lang
                                .substring(3)
                                .toUpperCase();
                        }
                        return [2 /*return*/, lang];
                }
            });
        });
    };
    LanguageService.prototype.registerHandlers = function () {
        var _this = this;
        window.queryBus.subscribe(LANGUAGE_QUERY, function () {
            return _this.language;
        });
        window.queryBus.subscribe(SINGLE_LANGUAGE_QUERY, function () {
            return _this.isSingleLanguage;
        });
        window.commandBus.subscribe(LANGUAGE_COMMAND_CHANGE, function (event) { return __awaiter(_this, void 0, void 0, function () {
            var url, redirectUri;
            return __generator(this, function (_a) {
                if (!event.payload) {
                    console.warn('Missing language in the payload');
                    return [2 /*return*/];
                }
                this.language = event.payload;
                setLanguage(this.language);
                this.saveUserLanguage(this.language);
                window.localStorage.setItem(USER_LANGUAGE_KEY, this.language);
                if (window.kcContext) {
                    setKeycloakLocale(this.language);
                    url = new URL(window.location.href);
                    redirectUri = url.searchParams.get('redirect_uri');
                    window.commandBus.publish(USER_LOGIN_KEYCLOAK, {
                        url: getUriWithLanguage(redirectUri, this.language),
                    });
                }
                this.publishLanguage();
                return [2 /*return*/];
            });
        }); });
        window.commandBus.register(LANGUAGE_COMMAND_CHANGE, {
            type: 'string',
            enum: Object.values(Language),
        });
        window.eventBus.register(LANGUAGE_UPDATED, {
            type: 'string',
            enum: Object.values(Language),
        });
    };
    LanguageService.prototype.initialize = function () {
        var _this = this;
        setLanguage(this.language);
        setTranslations({});
        this.publishLanguage().then(function () {
            window.queryBus.subscribe(VUE_I_18N_QUERY, function () {
                return _this.i18n;
            });
        });
    };
    LanguageService.prototype.publishLanguage = function () {
        window.eventBus.publish(LANGUAGE_UPDATED, this.language);
        return this.updateTranslations();
    };
    LanguageService.prototype.updateTranslations = function () {
        return __awaiter(this, void 0, void 0, function () {
            var translations;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, queryMappedTranslations()];
                    case 1:
                        translations = _a.sent();
                        this.i18n.locale = this.language;
                        this.i18n.setLocaleMessage(this.language, translations);
                        setTranslations(this.i18n.messages[this.language]);
                        return [2 /*return*/];
                }
            });
        });
    };
    LanguageService.prototype.saveUserLanguage = function (lang) {
        return __awaiter(this, void 0, void 0, function () {
            var user;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        user = magic.session.getUser();
                        if (!user) return [3 /*break*/, 2];
                        return [4 /*yield*/, magic.methods.users.updateLanguage(lang)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    return LanguageService;
}());
function i18nQuery() {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, window.queryBus.query(VUE_I_18N_QUERY)];
                case 1: return [2 /*return*/, (_a.sent())];
            }
        });
    });
}
function getUriWithLanguage(redirectUri, language) {
    var newRedirectUri = redirectUri === null || redirectUri === void 0 ? void 0 : redirectUri.replace(/\/[a-z]{2}(-[A-Z]{2})?\b/, "/" + language);
    return newRedirectUri;
}

export { LanguageService, USER_LANGUAGE_KEY, getUriWithLanguage, i18nQuery };
